/**
 * Pre-shell resolver for the active dimension's value selection (DP-3188).
 *
 * Answers one question before `<DiscoAppShell>` mounts: does this session
 * already know which dimension values the actor works in? Three answers, and
 * the shell waits for one of them — `resolving` (still deciding), `picker`
 * (ask, blocking), `ready` (proceed). It runs after `useConfigureLocale`
 * reports `localeReady` and after `/me` resolves `activeDimension`, and before
 * the shell renders, so the sidebar's `RecordAccessSelector` never paints in an
 * unresolved-storage state (research brief, "Where the picker lives in the
 * shell mount order").
 *
 * The precedence is URL → storage → picker, in that order, and it is the whole
 * substance of this hook:
 *
 * `allAccess` is deliberately **not** a short-circuit. An unfiltered actor is
 * still asked, because the picker answers "what do you want to look at", not
 * "what are you allowed to see" — and for an admin those differ. Skipping them
 * left the one group who most needs a narrowing control without one, staring at
 * every value in the tenant. They can still answer "all", which commits the
 * `__ALL__` sentinel and a paramless URL, so the unfiltered view stays one
 * click away.
 *
 * 1. No `activeDimension` — enforcement disabled, or a BE that predates it.
 * 2. A dimension param already on the URL. **Authoritative, and read-only**:
 *    a link, a bookmark, or a deep link states the scope for this navigation
 *    and must not be written back into storage, which records *choices*.
 * 3. Options still loading — nothing can be decided against an unknown reach.
 * 4. Zero reach: no options, so no selection exists to make (Q5). Downstream
 *    keeps reading the absent param as "all", which is vacuously true here.
 * 5. Exactly one option: the choice is forced, so make it silently (Q5).
 * 6. Storage says `__ALL__`: all-selected is paramless in the URL, so there is
 *    nothing to hydrate — the state is already correct (Q9).
 * 7. Storage names ids: hydrate the survivors into the URL. Ids the actor can
 *    no longer reach drop out; if nothing survives, the stored choice is void
 *    and we ask again.
 * 8. Nothing persisted, or all of it stale: ask.
 *
 * Every write goes through `useDimensionSelection`, so hydration and the
 * picker's commit share the sidebar's exact semantics — alphabetised param,
 * `{ replace: true }`, paramless-plus-sentinel for all-selected, and the
 * storage mirror in the same step. No URL or storage write is spelled out
 * twice in this codebase.
 *
 * The gate is a **one-shot per mount**. Once it has hydrated or taken the
 * user's pick it stops re-deciding: the URL is the render-time source of truth
 * from then on, and the sidebar owns every later change. It gets there one of
 * two ways, and which one matters:
 *
 * - **A param-ful pick resolves through step 3**, not through the latch. The
 *   commit puts the param on the URL and the gate hands over only once the
 *   router has observed it. Latching directly would hand over in the same React
 *   batch as the commit, while `useLocation()` still reported the pre-commit
 *   search — and the shell's landing redirect, mounting right then, would merge
 *   that empty search and replace the URL without the scope just chosen.
 * - **A paramless commit latches**, because there is no param for step 3 to
 *   see: the single-option hydrate of step 6 and the all-selected sentinel both
 *   commit a paramless URL. The latch is load-bearing there — a re-decide would
 *   re-commit forever — and it is what keeps the picker from reappearing on
 *   every render when `localStorage` is unavailable and the pick cannot be
 *   persisted (AC7: storage off degrades to a per-session gate, not to a loop).
 *
 * Two of PRD §9's three events come from here — `picker_shown` on entry into
 * step 9, `picker_committed` on the answer — and between them they are the
 * first-login funnel. The steps that resolve without asking emit nothing:
 * hydration and the forced single-value commit of step 6 are the gate deciding
 * on the actor's behalf, so counting them as scope changes would overstate both
 * the funnel and the sidebar's traffic (hence `emitSidebarCommits: false`).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  useAccessDimensionOptions,
  type AccessDimensionOption,
} from '../api/endpoints/useAccessDimensionOptions';
import { useCurrentUser } from '../api/endpoints/useCurrentUser';
import { extractAccessDimensionParam } from '../utils/accessDimensionQueryParam';
import {
  trackDimensionPickerCommitted,
  trackDimensionPickerShown,
} from '../utils/dimensionScopeO11y';
import {
  ALL_DIMENSION_VALUES,
  readDimensionScope,
} from '../utils/dimensionScopeStorage';
import type { DimensionScopeKeyParts } from '../utils/storageKeys';
import { useClientId } from './useClientId';
import { useCurrentEntityName } from './useCurrentEntityName';
import { useCurrentUsecase } from './useCurrentUsecase';
import { useDimensionSelection } from './useDimensionSelection';
import { useEntityMetadata } from '../api/endpoints/useEntityMetadata';

export type GateStatus =
  | { kind: 'resolving' }
  | {
      kind: 'picker';
      options: readonly AccessDimensionOption[];
      /** `capitalize(activeDimension.name)` — see {@link capitalize}. */
      label: string;
    }
  | { kind: 'ready' };

/** What the picker hands back: an explicit set, or "all values, incl. later ones". */
export type DimensionPick = Set<string> | typeof ALL_DIMENSION_VALUES;

export interface DimensionValueGate {
  status: GateStatus;
  /** Commit the picker's answer. Moves the gate to `ready`. */
  pick: (next: DimensionPick) => void;
}

/**
 * `hydrate` is internal: it is a decision to write, not a state the shell can
 * render. It surfaces as `resolving` until the write lands.
 */
type GateDecision = GateStatus | { kind: 'hydrate'; values: readonly string[] };

// Module-level singletons so a `resolving`/`ready` status keeps a stable
// identity across renders — a consumer may memoize on it.
const RESOLVING: GateStatus = { kind: 'resolving' };
const READY: GateStatus = { kind: 'ready' };

/**
 * The dimension's display label, derived from its `name` — the BE transmits no
 * label (HLD v0.3.1 §A). Backend-driven data, so deliberately not
 * `t()`-wrapped per the repo i18n convention; the picker interpolates it into a
 * `t()` sentence rather than concatenating onto one.
 *
 * Singular here, unlike `RecordAccessSelector`'s pluralized trigger label: the
 * picker asks "Which {label} do you want to work in?", which reads as one
 * choice even when several values are selectable.
 */
const capitalize = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

/**
 * Steps 6 and 7 of the precedence chain: what a previously stored selection
 * still entitles the actor to.
 *
 * `null` means storage had nothing usable — either it was empty, or every id in
 * it has since become unreachable, which makes the choice void rather than
 * merely narrow. The caller falls through to the picker.
 */
function decideFromStoredScope(
  storageParts: DimensionScopeKeyParts | null,
  options: readonly AccessDimensionOption[]
): GateDecision | null {
  const stored = storageParts ? readDimensionScope(storageParts) : null;
  if (!stored) {
    return null;
  }
  if (stored.values === ALL_DIMENSION_VALUES) {
    return READY;
  }
  if (!Array.isArray(stored.values)) {
    return null;
  }

  const reachable = new Set(options.map(option => option.id));
  const survivors = stored.values.filter(id => reachable.has(id));
  return survivors.length > 0 ? { kind: 'hydrate', values: survivors } : null;
}

/**
 * Step 1a: does the surface the actor is on consume the scope at all?
 *
 * `null` means "it does — carry on with the precedence chain". A status means
 * the chain stops here, and the question is either not worth asking on this
 * surface or not yet answerable.
 *
 * Pure and separate from the decision memo for two reasons: it keeps that memo
 * under the complexity ceiling (typescript:S1541), and it makes the flag
 * precedence directly testable — the `??` below is load-bearing, since
 * `applyTeamFilter` is the deprecated alias and still the only key deployed
 * backends send, while an explicit new-key `true` must win over a legacy
 * `false`.
 */
function decideFromSurface({
  surfaceIsScoped,
  isMetadataLoading,
  gridView,
}: {
  surfaceIsScoped: boolean;
  isMetadataLoading: boolean;
  gridView?: { applyRecordAccessFilter?: boolean; applyTeamFilter?: boolean };
}): GateStatus | null {
  // No entity on the route — a custom app, settings, the account page. Nothing
  // here reads the scope, so blocking it on the answer helps nobody.
  if (!surfaceIsScoped) {
    return READY;
  }
  // The flag is only trustworthy once its document has landed; asking against a
  // half-loaded response would ask on surfaces about to say they opted out.
  if (isMetadataLoading) {
    return RESOLVING;
  }
  // Only an explicit `false` opts out. Absent metadata, absent flags, or a
  // failed read all leave an entity route scoped — it is a scoped surface by
  // definition, and defaulting the other way would silently drop the selection
  // whenever metadata is flaky.
  const optedOut =
    (gridView?.applyRecordAccessFilter ?? gridView?.applyTeamFilter) === false;
  return optedOut ? READY : null;
}

export function useDimensionValueGate(): DimensionValueGate {
  const clientId = useClientId();
  const {
    activeDimension,
    userId,
    isLoading: isUserLoading,
  } = useCurrentUser(clientId);
  const [searchParams] = useSearchParams();

  // Personnel entity id — the only user id the storage key accepts (Q1); the
  // Auth0 identity's `'unknown'` fallback would collapse distinct users onto
  // one entry. Read through `useCurrentUser`'s accessor, which knows the id
  // lives at `data.entityData.id`. Empty until `/me` lands, which the loading
  // guard below covers.
  const dimensionName = activeDimension?.name ?? '';

  const { data: options, isLoading: areOptionsLoading } =
    useAccessDimensionOptions(clientId, dimensionName);

  /**
   * Does the surface the actor is actually looking at consume the scope?
   *
   * The question is only worth asking where the answer changes what is on
   * screen. A custom app (`/apps/{name}` — the nav configurator edits one
   * per-client singleton), settings, the account page and analytics are not
   * record-scoped, so asking there blocks a page the answer cannot affect.
   *
   * This reads the same `(entity, usecase)` metadata document, through the same
   * query key, that `RecordAccessSelector` already reads to decide whether to
   * render itself — so it is a cache hit rather than a second request, and the
   * picker and the selector finally agree about which surfaces are scoped.
   * Before this, a page could hide the selector because record access does not
   * apply while the picker still blocked that very page.
   *
   * Both flag keys, new one first: `applyTeamFilter` is the deprecated alias
   * and is still the only key deployed backends send. Reading only the new key
   * is what previously made the selector show on opted-out entities.
   */
  const entityName = useCurrentEntityName();
  const usecase = useCurrentUsecase();
  const { data: metadataResponse, isLoading: isMetadataLoading } =
    useEntityMetadata(clientId, entityName, { usecase });
  // A route with no entity is not scoped at all — there is no metadata to
  // consult and nothing on it reads the dimension. Distinguished from "an
  // entity that simply has no flag", which stays scoped by default.
  const surfaceDecision = decideFromSurface({
    surfaceIsScoped: Boolean(entityName),
    isMetadataLoading,
    gridView: metadataResponse?.data?.frontend?.gridView,
  });

  const { commit, commitAll } = useDimensionSelection({
    options,
    activeDimension,
    clientId,
    userId,
    // None of this instance's writes is a sidebar interaction: two of the three
    // are the gate deciding for the actor (hydration, the forced single value),
    // and the third is the picker, which reports `picker_committed` below.
    // Leaving the default on would file every stored-scope page load under
    // "re-scoped from the sidebar" (PRD §9).
    emitSidebarCommits: false,
  });

  /**
   * Latched once this mount has resolved the scope itself — by hydrating or by
   * taking the user's pick. See the one-shot note in the module docblock.
   */
  const [settled, setSettled] = useState(false);

  const storageParts = useMemo<DimensionScopeKeyParts | null>(
    () =>
      clientId && userId && dimensionName
        ? { clientId, userId, dimensionName }
        : null,
    [clientId, userId, dimensionName]
  );

  const decision = useMemo<GateDecision>(() => {
    if (settled) {
      return READY;
    }

    // Precondition rather than a precedence rule: until `/me` answers, an
    // absent `activeDimension` means "unknown", not "no enforcement" — and
    // reading it as the latter would let the shell mount unresolved, which is
    // the single thing this gate exists to prevent.
    if (isUserLoading) {
      return RESOLVING;
    }

    if (!activeDimension) {
      return READY; // (1)
    }

    // (1a) Stop if this surface does not consume the scope. `READY` here is
    // deliberately *not* a latch — `settled` is set only by a `hydrate` — so
    // navigating on to a scoped page re-runs every rule below and asks there
    // instead. The question is deferred, not skipped.
    if (surfaceDecision) {
      return surfaceDecision;
    }
    // (2) Read through the sanctioned reader, so a param that fails its tamper
    // guard counts as absent and cannot short-circuit the gate with junk.
    if (
      extractAccessDimensionParam({
        url: searchParams,
        dimensionName: activeDimension.name,
      }) !== null
    ) {
      return READY;
    }

    if (areOptionsLoading) {
      return RESOLVING; // (4)
    }
    if (options.length === 0) {
      return READY; // (4)
    }
    if (options.length === 1) {
      // (5) `commit` normalises a full selection to a paramless URL plus the
      // `__ALL__` mirror, which is exactly right here: with one reachable
      // value, "that value" and "all values" are the same selection, and
      // paramless is the wire contract `encore-grid` reads. The sentinel also
      // keeps the actor covered if the dimension grows a second value later —
      // this was never a choice they made, so silently widening beats
      // silently pinning them to one row.
      return { kind: 'hydrate', values: [options[0].id] };
    }

    const fromStorage = decideFromStoredScope(storageParts, options);
    // (6) (7)
    if (fromStorage) {
      return fromStorage;
    }

    return {
      kind: 'picker',
      options,
      label: capitalize(activeDimension.name),
    }; // (8)
  }, [
    settled,
    isUserLoading,
    activeDimension,
    surfaceDecision,
    searchParams,
    areOptionsLoading,
    options,
    storageParts,
  ]);

  // The one write the gate makes on its own. `settled` short-circuits the memo,
  // so a `hydrate` decision can only be seen once — and latching before the
  // commit means the URL round trip is not what stops the loop.
  useEffect(() => {
    if (decision.kind !== 'hydrate') {
      return;
    }
    setSettled(true);
    commit(new Set(decision.values));
  }, [decision, commit]);

  /**
   * `pick` is what the picker receives as `onPick`, so
   * `dimension_scope.picker_committed` is emitted here rather than inside
   * `DimensionValuePicker`: the payload names the client, the actor and the
   * dimension, and the gate is where all three already live. Threading them
   * through `GateStatus` and the shell's call site purely so the component
   * could emit would move the analytics without moving the data.
   */
  const pick = useCallback(
    (next: DimensionPick) => {
      const all = next === ALL_DIMENSION_VALUES;
      trackDimensionPickerCommitted({
        dimensionName,
        clientId,
        userId,
        kind: all ? 'all' : 'partial',
        // The sentinel covers the dimension, not the rows ticked to reach it,
        // so its size is the whole reach — same rule the commit itself follows.
        size: all ? options.length : next.size,
      });
      // A commit that lands a param on the URL needs no latch: precedence step
      // 3 reads that param as resolved, so the gate hands over only once the
      // *router* has observed the commit. Latching here instead would flip to
      // `ready` in the same batch as the commit, and the shell — including the
      // landing redirect — would mount while `useLocation()` still reported the
      // pre-commit search. The redirect then merged an empty query and replaced
      // the URL without the scope the actor had just chosen: the param was
      // written to `history` and dropped a tick later. Holding the picker for
      // the one extra render costs nothing visible and removes the race.
      //
      // An all-selected or whole-reach commit is deliberately paramless (the
      // sentinel lives in storage), so there is nothing for step 3 to see and
      // nothing a redirect could drop — those latch immediately, as before.
      const paramless = all || next.size === options.length;
      if (paramless) {
        setSettled(true);
      }

      if (all) {
        commitAll();
        return;
      }
      commit(next);
    },
    [commit, commitAll, dimensionName, clientId, userId, options.length]
  );

  // A pending hydration is not a state the shell can render: it reads as
  // `resolving` for the one tick before the write lands.
  const status: GateStatus = decision.kind === 'hydrate' ? RESOLVING : decision;

  /**
   * Emitted on entry into `picker`, not on every render of it — the shell holds
   * the screen for as long as the actor takes to answer. The ref carries the
   * previous kind rather than a "have I ever" boolean so the event still
   * describes a transition if the gate is ever re-entered.
   */
  const lastKindRef = useRef<GateStatus['kind'] | null>(null);
  useEffect(() => {
    const previous = lastKindRef.current;
    lastKindRef.current = status.kind;
    if (status.kind !== 'picker' || previous === 'picker') {
      return;
    }
    trackDimensionPickerShown({
      dimensionName,
      clientId,
      userId,
      optionCount: status.options.length,
    });
  }, [status, dimensionName, clientId, userId]);

  return { status, pick };
}
